import { useMemo } from 'react';
import { format } from 'date-fns';
import { REGULAR_DATE_FORMAT } from '@/const';
import { ASSET_STUDIO } from '@/types/shared/navigation-tabs.enums';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/shared/display/Icon';
import { MicroscopeIcon } from '@/assets/Icons';
import CardTitle from '@/components/uiKit/Cards/CardTitle';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';
import { formatAssetName } from '@/utils/shared';

interface DiagnosticsHeaderProps {
  selectedComponent: string;
  selectedDiagnostic: any;
  diagnostics?: any[];
  selectedData?: any;
  selectedComponents?: any;
  asset?: any;
}

export const DiagnosticsHeader = ({
  selectedComponent,
  selectedDiagnostic,
  diagnostics,
  selectedData,
  selectedComponents,
  asset,
}: DiagnosticsHeaderProps) => {
  const { isMobileAsset } = useActiveAssetsType();

  // Get the asset name for the title when viewing all components
  const assetName = useMemo(() => {
    // First try from asset prop
    const nameFromAsset = formatAssetName(asset, isMobileAsset);
    if (nameFromAsset) return nameFromAsset;

    // Fallback: try to get from selectedDiagnostic (for fixed assets)
    const assetFromDiagnostic = selectedDiagnostic?.[1]?.assets?.[0];
    if (assetFromDiagnostic) {
      return (
        assetFromDiagnostic.asset_ui_name ||
        assetFromDiagnostic.asset_name ||
        assetFromDiagnostic.unit_ui_name ||
        assetFromDiagnostic.unit_name
      );
    }

    return '';
  }, [asset, isMobileAsset, selectedDiagnostic]);

  // Get the component name for the title
  const currentComponentName = useMemo(() => {
    if (selectedComponent === ASSET_STUDIO.ALL_COMPONENTS) {
      return '';
    }

    // Try to get component name from diagnostics (for FixedAssets)
    if (diagnostics && diagnostics.length > 0) {
      const firstDiagnostic = diagnostics[0];
      if (firstDiagnostic?.component) {
        const name = firstDiagnostic.component.component_ui_name || firstDiagnostic.component.component_name;
        if (name) return name;
      }
    }

    // Try to get component name from selectedData (for Observations)
    if (selectedData) {
      if (Array.isArray(selectedData) && selectedData.length > 0) {
        const name = selectedData[0]?.component_ui_name || selectedData[0]?.component_name;
        if (name) return name;
      } else if (!Array.isArray(selectedData)) {
        const name = selectedData.component_ui_name || selectedData.component_name;
        if (name) return name;
      }
    }

    // Try to get from components in selectedDiagnostic
    const comp = selectedDiagnostic?.[1]?.assets?.[0]?.components?.find(
      (c: any) => c.component_id === selectedComponent
    );
    if (comp) {
      const name = comp.component_ui_name || comp.component_name;
      if (name) return name;
    }

    // Try selectedComponents
    if (selectedComponents?.data && selectedComponents.data.length > 0) {
      const name = selectedComponents.data[0]?.component_ui_name || selectedComponents.data[0]?.component_name;
      if (name) return name;
    }

    // Final fallback
    return 'Component';
  }, [selectedComponent, diagnostics, selectedData, selectedDiagnostic, selectedComponents]);

  // Get diagnostic date
  const diagnosticDate = selectedDiagnostic?.[0]
    ? format(new Date(selectedDiagnostic[0]), REGULAR_DATE_FORMAT)
    : format(new Date(), REGULAR_DATE_FORMAT);

  // Get health status for background color
  const healthStatus = useMemo(() => {
    if (selectedComponent === ASSET_STUDIO.ALL_COMPONENTS) {
      return selectedDiagnostic?.[1]?.assets?.[0]?.health_status;
    }

    // Try diagnostics first (for FixedAssets)
    if (diagnostics && diagnostics.length > 0 && diagnostics[0]?.health_status) {
      return diagnostics[0].health_status;
    }

    // Try selectedData (for Observations)
    if (selectedData) {
      if (Array.isArray(selectedData) && selectedData.length > 0 && selectedData[0]?.health_status) {
        return selectedData[0].health_status;
      } else if (!Array.isArray(selectedData) && selectedData?.health_status) {
        return selectedData.health_status;
      }
    }

    // Try to get from components in selectedDiagnostic
    const comp = selectedDiagnostic?.[1]?.assets?.[0]?.components?.find(
      (c: any) => c.component_id === selectedComponent
    );
    if (comp?.health_status) {
      return comp.health_status;
    }

    return undefined;
  }, [selectedComponent, selectedDiagnostic, diagnostics, selectedData]);

  const currentDiagnosticHealthStatus =
    healthStatus === 'healthy'
      ? 'bg-healthy-light'
      : healthStatus === 'alarm'
      ? 'bg-alarm-light'
      : healthStatus === 'monitor'
      ? 'bg-monitor-light'
      : healthStatus === 'critical'
      ? 'bg-critical-light'
      : healthStatus === 'na' || healthStatus === 'not-monitored'
      ? 'bg-notMonitored-light'
      : '';

  return (
    <div className="flex items-center gap-2">
      <Icon icon={MicroscopeIcon} className="min-w-6" />
      <CardTitle
        text={`Diagnosed Faults for ${
          selectedComponent === ASSET_STUDIO.ALL_COMPONENTS ? assetName || 'Asset' : currentComponentName
        }`}
        className="gap-0"
      />
      <span className={cn('px-4 py-1 rounded-lg font-semibold text-sm', currentDiagnosticHealthStatus)}>
        {diagnosticDate}
      </span>
    </div>
  );
};
